import { useEffect, useState } from "react";
import { Loader2, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const areaCls = "w-full min-h-[180px] px-4 py-3 rounded-md bg-[#0B0F17] border border-[#242C3D] focus:border-indigo-500 focus:outline-none text-sm font-mono transition-colors";
const toLines = (arr) => (arr || []).join("\n");
const fromLines = (t) => t.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

export default function Settings() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [domains, setDomains] = useState("");
  const [emails, setEmails] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/settings").then((r) => {
      if (!live) return;
      setDomains(toLines(r.data.test_domains)); setEmails(toLines(r.data.test_emails)); setLoaded(true);
    }).catch((e) => live && toast.error(formatErr(e)));
    return () => { live = false; };
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings", { test_domains: fromLines(domains), test_emails: fromLines(emails) });
      setDomains(toLines(r.data.test_domains));
      setEmails(toLines(r.data.test_emails));
      toast.success("Test lead rules saved — stats and reports updated");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 space-y-10" data-testid="settings-page">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Workspace</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Settings</h1>
      </div>
      <section className="rounded-lg border border-[#242C3D] bg-[#141A24]/60 p-8 space-y-6">
        <div className="flex items-start gap-3">
          <ShieldOff className="w-5 h-5 text-amber-300 mt-0.5" />
          <div>
            <h2 className="font-display font-semibold text-lg">Test & internal leads</h2>
            <p className="text-sm text-slate-400 mt-1">Leads matching these rules are left out of every campaign's stats, Reports, client reports and CSV exports.
              They stay in each campaign's leads table behind "Show test leads" — nothing is deleted.</p>
          </div>
        </div>
        {!canEdit && <p data-testid="settings-readonly-note" className="text-sm text-amber-300/90 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3">Only admins can change these rules. You can view them here.</p>}
        {!loaded ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Domains (one per line)</label>
              <textarea data-testid="settings-test-domains" readOnly={!canEdit} className={areaCls} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder={"yourcompany.com\nagency.io"} />
              <p className="text-xs text-slate-500 mt-1.5">Any email ending in @domain is treated as a test lead.</p>
            </div>
            <div>
              <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Exact emails (one per line)</label>
              <textarea data-testid="settings-test-emails" readOnly={!canEdit} className={areaCls} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder={"qa@gmail.com"} />
              <p className="text-xs text-slate-500 mt-1.5">For one-off leads use "Mark as test" in a campaign's leads table.</p>
            </div>
          </div>
        )}
        {canEdit && <button type="button" data-testid="settings-save-button" onClick={save} disabled={busy || !loaded}
          className="h-10 px-6 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save rules
        </button>}
      </section>
    </div>
  );
}
